// src/frontend/js/modern/modules/multi-list/store.js
import { stateCache } from '../../core/state-cache.js';
import { displayStore } from '../../core/storage/display-store.js';
import { BaseStore , normalizeItems } from '../../shared/store-helpers.js';
import { validators } from '../../shared/validators.js';

const LIST_SPECIFIC_FIELDS = [
	'remove_url', 'remove_nonce', 'wishlist_token',
	'list_name', 'item_id', 'wishlist_id'
];

class MultiListStore extends BaseStore {
	constructor() {
		super('multiList', 'wishlist_id');
	}

	/* ═══ Override getters for list-based structure ═══ */

	get items() { return { get: () => stateCache.getLists() }; }
	get lists() { return { get: () => stateCache.getLists() }; }
	get count() { return { get: () => stateCache.getCount('multiList') }; }

	/* ═══ List-specific queries ═══ */

	getList(listId) {
		return stateCache.getList(String(listId)) || null;
	}

	hasList(listId) {
		return !!stateCache.getList(String(listId));
	}

	hasProductInList(listId, productId) {
		const list = this.getList(listId);
		if (!list) return false;
		return (list.wishlist_items || []).some(
			i => String(i.product_id) === String(productId)
		);
	}

	getListsByProduct(productId) {
		return stateCache.getLists().filter(
			l => (l.wishlist_items || []).some(
				i => String(i.product_id) === String(productId)
			)
		);
	}

	getProductListCount(productId) {
		return this.getListsByProduct(productId).length;
	}

	isPendingList(listId) {
		return String(listId).startsWith('temp_');
	}

	getRealListId(tempId) {
		const list = this.getList(tempId);
		if (list && !this.isPendingList(list.wishlist_id)) {
			return list.wishlist_id;
		}
		return null;
	}

	getUserLists(sourceLists = null, excludeDefault = null) {
		const lists = sourceLists || stateCache.getLists();
		const isMerge = validators.isTrue(window.wlfmc_l10n?.merge_lists);
		const shouldExclude = excludeDefault !== null
			? validators.isTrue(excludeDefault)
			: !isMerge;
		if (!shouldExclude) return [...lists];
		return lists.filter(list => !validators.isTrue(list.is_default));
	}

	/* ═══ List CRUD ═══ */

	addList(listData) {
		return stateCache.addList(listData);
	}

	updateList(listId, updates) {
		return stateCache.updateList(String(listId), updates);
	}

	removeList(listId) {
		return stateCache.removeList(String(listId));
	}

	/* ═══ Item-in-list operations ═══ */

	addItemToList(listId, productId, data = {}) {
		return stateCache.addItemToList(String(listId), String(productId), data);
	}

	removeItemFromList(listId, productId) {
		return stateCache.removeItemFromList(String(listId), String(productId));
	}

	confirmAddItem(listId, productId, serverData) {
		const list = this.getList(listId);
		if (!list) return;
		const items = list.wishlist_items || [];
		const idx = items.findIndex(
			i => String(i.product_id) === String(productId) && i._optimistic
		);
		if (idx === -1) return;

		const updated = [...items];
		const display = serverData?.display || {};

		const mergedData = { ...updated[idx] };
		// Promote from serverData top level
		for (const key of LIST_SPECIFIC_FIELDS) {
			if (serverData?.[key] !== undefined) mergedData[key] = serverData[key];
		}
		// Promote from serverData.display if missing
		for (const key of LIST_SPECIFIC_FIELDS) {
			if (mergedData[key] === undefined && display[key] !== undefined) {
				mergedData[key] = display[key];
			}
		}
		mergedData._optimistic = false;

		updated[idx] = mergedData;
		stateCache.updateList(String(listId), { wishlist_items: updated });
	}

	rollbackAddItem(listId, productId) {
		const list = this.getList(listId);
		if (!list) return;
		const filtered = (list.wishlist_items || []).filter(
			i => !(String(i.product_id) === String(productId) && i._optimistic)
		);
		stateCache.updateList(String(listId), { wishlist_items: filtered });
	}

	/* ═══ Override clear ═══ */

	clear() {
		stateCache.setModuleData('multiList', [], 0);
	}

	/* ═══ Override syncFromData for nested list structure ═══ */

	/**
	 * Multi-list sync strips display data from nested wishlist_items
	 * and routes it to the DisplayStore under the 'multiList' namespace.
	 */
	syncFromData(data) {
		let lists = (data.lists || []).map(list => ({
			...list,
			wishlist_items: normalizeItems(list.wishlist_items).map(item => {
				if (item?.display && item.product_id !== undefined) {
					displayStore.set('multiList', item.product_id, item.display);
				}
				const { display, ...rest } = item;
				if (display) {
					for (const key of LIST_SPECIFIC_FIELDS) {
						if (display[key] !== undefined && rest[key] === undefined) {
							rest[key] = display[key];
						}
					}
				}
				return rest;
			}),
		}));
		stateCache.setModuleData('multiList', lists, data.count || lists.length);
	}
}

export const multiListModuleStore = new MultiListStore();
export default multiListModuleStore;
